import {
  Alert,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { listBrokenMappings } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { CodelistVersion, MappingKind } from '../../api/types';
import { ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useSelectedTenant } from '../../tenant/tenantSelection';

export interface BrokenMappingsPanelProps {
  kind: MappingKind;
  /** The kind's active version, or null while none is — then nothing is checked and there is nothing to list. */
  activeVersion: CodelistVersion | null;
}

/**
 * The enabled mappings whose target the active version no longer accepts. The registry is shared,
 * so the mappings come from every tenant in scope — the selected one, or all of them while none is
 * selected — and each row names its tenant. Reported only: the server never rewrites a mapping, so
 * the fix happens on the Mappings page. The endpoint answers an empty list both when nothing is
 * broken and when no version is active, so the caller says which — the two read very differently.
 */
export function BrokenMappingsPanel({ kind, activeVersion }: BrokenMappingsPanelProps) {
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();

  const query = useQuery({
    queryKey: queryKeys.codelists.broken(selectedTenantGid, kind),
    queryFn: ({ signal }) => listBrokenMappings(kind, signal),
    enabled: activeVersion !== null,
  });

  if (activeVersion === null) {
    return <Alert severity="info">{t('codelists.versions.broken.noActive')}</Alert>;
  }

  const mappings = query.data?.find((item) => item.kind === kind)?.mappings ?? [];

  return (
    <Stack spacing={1.5}>
      <Typography variant="h6">
        {t('codelists.versions.broken.title', { version: activeVersion.versionLabel })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('codelists.versions.broken.description')}
      </Typography>

      {query.isPending && <LoadingBlock />}
      {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

      {query.isSuccess &&
        (mappings.length === 0 ? (
          <Alert severity="success">{t('codelists.versions.broken.none')}</Alert>
        ) : (
          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columns.tenant')}</TableCell>
                    <TableCell>{t('mappings.columns.sourceValue')}</TableCell>
                    <TableCell>{t('mappings.columns.targetValue')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((mapping) => (
                    <TableRow key={mapping.mappingGid} hover>
                      <TableCell>{mapping.tenantName}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{mapping.sourceValue}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{mapping.targetValue}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}
    </Stack>
  );
}
